import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Play } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";

const easeOut = [0.19, 1, 0.22, 1];

/**
 * BrandFilm section — displays an admin-managed brand film (video + title +
 * description + CTA). Renders nothing when no active brand_film media is set.
 */
export function BrandFilmSection() {
  const [item, setItem] = useState(null);
  const [playing, setPlaying] = useState(false);

  useEffect(() => {
    api
      .get("/homepage-media?kind=brand_film")
      .then(({ data }) => setItem((data || [])[0] || null))
      .catch(() => {});
  }, []);

  if (!item) return null;
  const src = item.desktop_video || item.mobile_video;
  const poster = item.desktop_image || item.mobile_image;

  return (
    <section className="border-t border-b bg-foreground text-background overflow-hidden" data-testid="brand-film-section">
      <div className="grid lg:grid-cols-[1.2fr_1fr] items-stretch min-h-[60vh]">
        <div className="relative min-h-[50vh] lg:min-h-[70vh] group">
          {src ? (
            <>
              <video
                key={playing ? "playing" : "poster"}
                src={src}
                poster={poster || undefined}
                autoPlay={playing}
                muted={!playing}
                loop
                playsInline
                controls={playing}
                preload="metadata"
                className="absolute inset-0 w-full h-full object-cover"
              />
              {!playing && (
                <button
                  onClick={() => setPlaying(true)}
                  className="absolute inset-0 grid place-items-center bg-black/20 hover:bg-black/30"
                  aria-label="Play brand film"
                  data-testid="brand-film-play"
                  style={{ transitionProperty: "background-color", transitionDuration: "200ms" }}
                >
                  <span className="h-16 w-16 rounded-full border border-white/70 grid place-items-center">
                    <Play className="h-6 w-6" fill="currentColor" />
                  </span>
                </button>
              )}
            </>
          ) : poster ? (
            <img src={poster} alt="Brand film" className="absolute inset-0 w-full h-full object-cover" />
          ) : null}
        </div>
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.8, ease: easeOut }}
          className="p-10 lg:p-16 flex flex-col justify-center"
        >
          {item.eyebrow && <p className="label-xs opacity-70">{item.eyebrow}</p>}
          {item.title && (
            <h2 className="mt-5 text-4xl lg:text-6xl font-serif tracking-tighter leading-[0.98]">{item.title}</h2>
          )}
          {item.subtitle && (
            <p className="mt-8 max-w-md leading-relaxed opacity-80">{item.subtitle}</p>
          )}
          {item.cta_label && item.cta_link && (
            <div className="mt-10">
              {item.cta_link.startsWith("/") ? (
                <Link to={item.cta_link}>
                  <Button className="rounded-none bg-white text-black hover:bg-white/90 px-10 py-6 label-xs">
                    {item.cta_label}
                  </Button>
                </Link>
              ) : (
                <a href={item.cta_link} target="_blank" rel="noreferrer">
                  <Button className="rounded-none bg-white text-black hover:bg-white/90 px-10 py-6 label-xs">
                    {item.cta_label}
                  </Button>
                </a>
              )}
            </div>
          )}
        </motion.div>
      </div>
    </section>
  );
}

/**
 * Lookbook — horizontal, magazine-style gallery driven by admin uploads.
 */
export function LookbookSection() {
  const [items, setItems] = useState([]);
  useEffect(() => {
    api
      .get("/homepage-media?kind=lookbook")
      .then(({ data }) => setItems(data || []))
      .catch(() => {});
  }, []);
  if (items.length === 0) return null;

  return (
    <section className="py-24 lg:py-32" data-testid="lookbook-section">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 mb-10 lg:mb-14 flex items-end justify-between">
        <div>
          <p className="label-xs opacity-60">Lookbook</p>
          <h2 className="mt-3 text-4xl lg:text-5xl font-serif tracking-tighter">Season in Motion.</h2>
        </div>
        <p className="hidden md:block label-xs opacity-60">
          Scroll → to explore
        </p>
      </div>
      <div className="overflow-x-auto no-scrollbar" data-testid="lookbook-scroller">
        <div className="flex gap-6 lg:gap-10 px-6 lg:px-12 pb-4">
          {items.map((it, i) => (
            <motion.figure
              key={it.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.7, ease: easeOut, delay: i * 0.06 }}
              className="shrink-0 w-[75vw] md:w-[420px] lg:w-[520px]"
              data-testid={`lookbook-item-${it.id}`}
            >
              <div className="relative aspect-[4/5] overflow-hidden bg-muted">
                {it.desktop_image && (
                  <img
                    src={it.desktop_image}
                    alt={it.title || "Lookbook"}
                    loading="lazy"
                    className="w-full h-full object-cover"
                  />
                )}
              </div>
              {(it.title || it.subtitle) && (
                <figcaption className="mt-5">
                  {it.eyebrow && <p className="label-xs opacity-60">{it.eyebrow}</p>}
                  {it.title && <p className="mt-1 font-serif text-2xl tracking-tight">{it.title}</p>}
                  {it.subtitle && <p className="text-sm opacity-70 mt-1">{it.subtitle}</p>}
                  {it.cta_label && it.cta_link && (
                    <Link to={it.cta_link} className="mt-3 inline-block label-xs link-underline">
                      {it.cta_label} →
                    </Link>
                  )}
                </figcaption>
              )}
            </motion.figure>
          ))}
        </div>
      </div>
    </section>
  );
}
